import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import feather from 'feather-icons';
import { Link, useNavigate } from 'react-router-dom';
import useOutletRefreshSignal from '../hooks/useOutletRefreshSignal';
import serverClient from '../api/ServerClient';
import { DeleteToken } from '../auth/Auth';
import Alert from '../components/Alert';
import Loader from '../components/Loader';
import { getNotificationLogo } from '../util/notificationHelper';
import BottomNavbar from '../components/Home/BottomNavbar';
import { useLanguage } from '../context/LanguageContext';
import { getUserTimezone } from '../util/helper';
import { getPageMeta } from '../util/seoHelper';
import useRefreshSignal from '../hooks/useRefreshSignal';

function NotificationPage() {
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const refreshSignal = useOutletRefreshSignal();
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [notificationData, setNotificationData] = useState(null);

    const fetchNotificationsData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await serverClient.get(`/api/user/notifications`);
            setNotificationData(response?.data?.data);
        } catch (e) {
            if (e.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
                navigate('/login');
            } else {
                setError(t('somethingWentWrong'));
            }
        } finally {
            setLoading(false);
        }
    }, [navigate, t]);
    useEffect(() => {
        fetchNotificationsData();
        feather.replace();
    }, [fetchNotificationsData]);

    useRefreshSignal(refreshSignal, () => {
        fetchNotificationsData();
    });

    return (
        <div className="notification-page">
            <Helmet>
                <title>{getPageMeta('notification', language).title}</title>
                <meta
                    name="description"
                    content={getPageMeta('notification', language).description}
                />
                <meta name="keywords" content={getPageMeta('notification', language).keywords} />
                <link rel="canonical" href={getPageMeta('notification', language).url} />
                {getPageMeta('notification', language).noindex && (
                    <meta name="robots" content="noindex, follow" />
                )}
            </Helmet>
            <header className="section-t-space">
                <div className="custom-container">
                    <div className="header-panel">
                        <Link to="/" className="back-btn">
                            <i className="icon" data-feather="arrow-left"></i>
                        </Link>
                        <h2>{t('notification')}</h2>
                        <div className="dropdown">
                            <button type="button" className="back-btn" data-bs-toggle="dropdown">
                                <i className="icon" data-feather="settings"></i>
                            </button>
                            <ul className="dropdown-menu">
                                <li>
                                    <Link className="dropdown-item" to="#">
                                        {t('muteNotification')}
                                    </Link>
                                </li>
                                <li>
                                    <Link className="dropdown-item" to="#">
                                        {t('markAsAllRead')}
                                    </Link>
                                </li>
                                <li>
                                    <Link className="dropdown-item" to="#">
                                        {t('removeAll')}
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </header>

            <div className="section-t-space custom-container section-b-space">
                <div className="profile-section">
                    <div className="profile-banner">
                        <div className="profile-image">
                            <img
                                className="img-fluid profile-pic"
                                src="/assets/images/icons/notification_2.png"
                                alt="Profile"
                                referrerPolicy="no-referrer"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Loader loading={loading} />
            {error && (
                <div className="p-4">
                    <Alert alert={'danger'}>{error}</Alert>
                </div>
            )}

            <section className="section-b-space">
                <div className="custom-container">
                    {(notificationData && Object.keys(notificationData).length) > 0 ? (
                        Object.keys(notificationData).map((date, index) => (
                            <div key={index} className="mb-3">
                                <div className="title mt-3">
                                    <h2>{date}</h2>
                                </div>
                                <ul className="notification-list">
                                    {notificationData[date].map((notif) => (
                                        <li className="notification-box" key={notif.id}>
                                            <div className="notification-img">
                                                <img
                                                    className="img-fluid icon transaction-icon"
                                                    src={getNotificationLogo(notif.type)}
                                                    alt="notification-icon"
                                                />
                                            </div>
                                            <div className="notification-details">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <h5 className="fw-semibold dark-text">
                                                            {notif.title}
                                                        </h5>
                                                        <h6 className="fw-normal light-text mt-1">
                                                            {t('system')}
                                                        </h6>
                                                    </div>
                                                    <h6 className="time fw-normal light-text">
                                                        {new Date(
                                                            notif.created_at
                                                        ).toLocaleTimeString('en-US', {
                                                            hour: '2-digit',
                                                            minute: '2-digit',
                                                            hour12: true,
                                                            timeZone: getUserTimezone(),
                                                        })}
                                                    </h6>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center mt-3">
                                                    <h5 className="dark-text fw-normal">
                                                        {notif.description}
                                                    </h5>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))
                    ) : !loading ? (
                        <>
                            <div className="empty-page">
                                <img
                                    className="notification-img custom-img-fluid transaction-icon"
                                    src={'/assets/images/svg/empty-notification-v2.svg'}
                                    alt="notification"
                                />
                                <h3 className="d-block fw-normal dark-text text-center mt-3">
                                    {t('noNewNotification')}
                                    <p>{t('noNotificationFound')}</p>
                                </h3>
                            </div>
                        </>
                    ) : null}
                </div>
            </section>

            <BottomNavbar />
        </div>
    );
}

export default NotificationPage;
